import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../hooks/useAuth';

export interface CartItem {
  id: number;
  title: string;
  price: number;
  quantity: number;
  image_url?: string;
  seller_id: number;
  weight_oz?: number; // optional weight in ounces
  quantity_available?: number; // Add this field
}

interface ShippingMethod {
  id: number;
  name: string;
  display_name: string;
  provider: string;
  service_code: string;
  description: string;
}

interface CartContextType {
  items: CartItem[];
  addToCart: (item: CartItem) => void;
  removeFromCart: (id: number) => void;
  updateQuantity: (id: number, quantity: number) => void;
  clearCart: () => void;
  itemCount: number;
  subtotal: number;
  shippingMethods: ShippingMethod[];
  selectedShippingMethod: ShippingMethod | null;
  setSelectedShippingMethod: (method: ShippingMethod) => void;
  shippingCost: number;
  total: number;
  isLoading: boolean;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

function readCart(key: string): CartItem[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((item): item is CartItem => {
      return item !== null && typeof item === 'object' && typeof (item as CartItem).id === 'number';
    });
  } catch {
    return [];
  }
}

function mergeCartItems(userItems: CartItem[], guestItems: CartItem[]): CartItem[] {
  const byId = new Map<number, CartItem>();
  for (const item of userItems) {
    byId.set(item.id, item);
  }
  for (const guest of guestItems) {
    const existing = byId.get(guest.id);
    if (!existing) {
      byId.set(guest.id, guest);
      continue;
    }
    if (Number(guest.quantity) > Number(existing.quantity)) {
      byId.set(guest.id, guest);
    }
  }
  return Array.from(byId.values());
}

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  
  const [items, setItems] = useState<CartItem[]>(() => {
    // Load cart from localStorage on initialization with user-specific key
    const cartKey = user ? `cart_${user.id}` : 'cart_guest';
    const savedCart = localStorage.getItem(cartKey);
    return savedCart ? JSON.parse(savedCart) : [];
  });

// Shipping related state
const [shippingMethods, setShippingMethods] = useState<ShippingMethod[]>([]);
const [selectedShippingMethod, setSelectedShippingMethod] = useState<ShippingMethod | null>(null);
const [shippingCost, setShippingCost] = useState<number>(0);
const [isLoading, setIsLoading] = useState<boolean>(false);

// Calculate total items and price whenever cart changes
const itemCount = items.reduce((count, item) => count + item.quantity, 0);

// Calculate subtotal (before tax and shipping)
const subtotal = items.reduce((sum, item) => {
  // Ensure price is treated as a number
  const itemPrice = typeof item.price === 'string' ? parseFloat(item.price) : Number(item.price);
  return sum + (itemPrice * item.quantity);
}, 0);

// Note: Tax will be calculated by Stripe during checkout
// Total is calculated as subtotal + shipping for display purposes only
  
  // Persist the cart, and fold a guest cart into the account cart on login.
  const prevUserIdRef = useRef<number | null | undefined>(undefined);
  const pendingMergeRef = useRef(false);
  useEffect(() => {
    const nextUserId = user?.id ?? null;
    const prevUserId = prevUserIdRef.current;
    prevUserIdRef.current = nextUserId;

    const becameUser = prevUserId === null && nextUserId !== null;
    const mountedAsUserWithGuest =
      prevUserId === undefined &&
      nextUserId !== null &&
      localStorage.getItem('cart_guest') !== null;

    if (becameUser || mountedAsUserWithGuest) {
      const guestItems = becameUser ? items : readCart('cart_guest');
      const userItems = readCart(`cart_${nextUserId}`);
      const merged = mergeCartItems(userItems, guestItems);
      localStorage.setItem(`cart_${nextUserId}`, JSON.stringify(merged));
      localStorage.removeItem('cart_guest');
      pendingMergeRef.current = true;
      setItems(merged);
      return;
    }

    // Strict Mode re-runs this effect before the merged state arrives. Skip that
    // write so the guest cart does not replace the account cart we just saved.
    if (pendingMergeRef.current && nextUserId !== null) {
      const stored = localStorage.getItem(`cart_${nextUserId}`);
      if (stored !== JSON.stringify(items)) {
        return;
      }
      pendingMergeRef.current = false;
    }

    const cartKey = nextUserId !== null ? `cart_${nextUserId}` : 'cart_guest';
    localStorage.setItem(cartKey, JSON.stringify(items));
  }, [items, user]);
  
  // Fetch shipping methods when component mounts
  useEffect(() => {
    const fetchShippingMethods = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get('/api/shipping/methods');
        setShippingMethods(response.data);
        
        // Set default shipping method to the first one if available
        if (response.data.length > 0) {
          setSelectedShippingMethod(response.data[0]);
        }
        
      } catch (error) {
        console.error('Error fetching shipping methods:', error);
        
        // Provide fallback shipping methods to ensure checkout is always available
        const fallbackShippingMethods: ShippingMethod[] = [
          {
            id: 1,
            name: 'USPS_PRIORITY',
            display_name: 'USPS Priority Mail',
            provider: 'USPS',
            service_code: 'PRIORITY',
            description: '2-3 business days delivery'
          },
          {
            id: 2,
            name: 'USPS_FIRST_CLASS',
            display_name: 'USPS First Class Mail',
            provider: 'USPS',
            service_code: 'FIRST_CLASS',
            description: '3-5 business days delivery'
          },
          {
            id: 3,
            name: 'USPS_MEDIA_MAIL',
            display_name: 'USPS Media Mail',
            provider: 'USPS',
            service_code: 'MEDIA_MAIL',
            description: '2-8 business days delivery'
          }
        ];
        
        setShippingMethods(fallbackShippingMethods);
        setSelectedShippingMethod(fallbackShippingMethods[0]);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchShippingMethods();
  }, []);
  
  // Calculate shipping cost when selected method or items change
  useEffect(() => {
    const calculateShipping = async () => {
      if (!selectedShippingMethod || items.length === 0) {
        setShippingCost(0);
        return;
      }
      
      try {
        setIsLoading(true);
        const response = await axios.post('/api/shipping/calculate', {
          items: items,
          shipping_method_id: selectedShippingMethod.id,
          to_zipcode: '90210' // This would normally come from the user's saved address
        });
        
        setShippingCost(Number(response.data.cost));
      } catch (error) {
        console.error('Error calculating shipping cost:', error);
        
        // Provide fallback shipping costs based on the selected method
        let fallbackCost = 0;
        if (selectedShippingMethod.service_code === 'PRIORITY') {
          fallbackCost = 8.50;
        } else if (selectedShippingMethod.service_code === 'FIRST_CLASS') {
          fallbackCost = 4.50;
        } else if (selectedShippingMethod.service_code === 'MEDIA_MAIL') {
          fallbackCost = 3.50;
        } else {
          fallbackCost = 5.00; // Default fallback
        }
        
        setShippingCost(fallbackCost);
      } finally {
        setIsLoading(false);
      }
    };
    
    calculateShipping();
  }, [selectedShippingMethod, items]);

  const addToCart = (newItem: CartItem) => {
    // Ensure price is a number before adding to cart
    const itemToAdd = {
      ...newItem,
      price: typeof newItem.price === 'string' ? parseFloat(newItem.price) : Number(newItem.price)
    };

    setItems(prevItems => {
      // Check if item already exists in cart
      const existingItemIndex = prevItems.findIndex(item => item.id === itemToAdd.id);
      
      if (existingItemIndex >= 0) {
        // If item exists, update quantity
        const updatedItems = [...prevItems];
        // Ensure we don't exceed quantity_available
        const currentItem = updatedItems[existingItemIndex];
        const maxQuantity = itemToAdd.quantity_available || currentItem.quantity_available || 1;
        const newQuantity = Math.min(currentItem.quantity + itemToAdd.quantity, maxQuantity);
        
        updatedItems[existingItemIndex] = {
          ...currentItem,
          quantity: newQuantity,
          // Update quantity_available if provided in the new item
          quantity_available: itemToAdd.quantity_available || currentItem.quantity_available
        };
        return updatedItems;
      } else {
        // If item doesn't exist, add it to cart
        return [...prevItems, itemToAdd];
      }
    });
  };

  const removeFromCart = (id: number) => {
    setItems(prevItems => {
      const newItems = prevItems.filter(item => item.id !== id);
      // If we've removed all items, also reset the shipping method selection
      if (newItems.length === 0) {
        setSelectedShippingMethod(null);
        setShippingCost(0);
      }
      return newItems;
    });
  };

  const updateQuantity = (id: number, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(id);
      return;
    }
    
    setItems(prevItems => 
      prevItems.map(item => {
        if (item.id === id) {
          // Ensure quantity doesn't exceed available quantity
          const maxQuantity = item.quantity_available || 1;
          const safeQuantity = Math.min(quantity, maxQuantity);
          return { ...item, quantity: safeQuantity };
        }
        return item;
      })
    );
  };

  const clearCart = () => {
    setItems([]);
    // Also clear from localStorage
    const cartKey = user ? `cart_${user.id}` : 'cart_guest';
    localStorage.removeItem(cartKey);
  };

  return (
    <CartContext.Provider value={{
      items,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      itemCount,
      subtotal,
      shippingMethods,
      selectedShippingMethod,
      setSelectedShippingMethod,
      shippingCost,
      total: subtotal + shippingCost, // Total is calculated as subtotal + shipping
      isLoading
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = (): CartContextType => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}; 